import React from 'react';
import { Grid, Typography } from '@material-ui/core';
import { Section, SmSpace } from 'm2m.components';
import { ModalidadeAmortizacao, NotaComercialApiResponse, TipoPagamento } from 'common/genericTypes';
import moment from 'moment';
import {
  percentualAmortizacaoDescriptions,
  tipoPagamentoDescriptions,
  convencaoCalendarioPagamentoDescriptions,
  periodicidadeFluxoPagamentoDescriptions,
} from 'common/genericDescriptions';
import { DataGrid, GridColumns, GridValueFormatterParams } from '@mui/x-data-grid';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { formatNumber } from 'utils/number';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  eventosFinanceiros: NotaComercialApiResponse;
}

export const DetalhesEventosFinanceiros: React.FC<Props> = (props) => {
  const { eventosFinanceiros } = props;

  const sortedAmortizacoes = eventosFinanceiros.amortizacoes
    ?.sort((a, b) => +moment(a.dataAmortizacao) - +moment(b.dataAmortizacao))
    .map((amortizacao) => ({
      id: uuidv4(),
      percentual: amortizacao.percentualAmortizacao,
      dataAmortizacao: amortizacao.dataAmortizacao,
    }));

  const sortedPagamentoJurosDates = eventosFinanceiros.pagamentoJuros?.datas
    ?.sort((a, b) => +moment(a) - +moment(b))
    .map((pagamentoJuros) => ({
      id: uuidv4(),
      dataPagamentoJuros: pagamentoJuros,
    }));

  const sortedIncorporacaoJurosDates = eventosFinanceiros.incorporacaoJuros?.datas?.sort(
    (a, b) => +moment(a) - +moment(b)
  );

  const subtituloAmortizacao =
    eventosFinanceiros.integracaoMonitor?.tipoPagamento === TipoPagamento.TabelaPrice
      ? 'Amortização e Juros'
      : 'Amortização';

  const [pageSize, setPageSize] = React.useState(20);

  const getAmortizacaoGridColumns: GridColumns = [
    {
      field: 'percentual',
      headerName: '% da Amortização',
      type: 'number',
      width: 180,
      editable: false,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 8, maximumFractionDigits: 8 });
      },
    },
    {
      field: 'dataAmortizacao',
      headerName: 'Data da Amortização',
      width: 250,
      type: 'date',
      editable: false,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
      },
    },
  ];

  const getPagamentoJurosGridColumns: GridColumns = [
    {
      field: 'dataPagamentoJuros',
      headerName: 'Data de Pagamento de Juros',
      width: 250,
      type: 'date',
      editable: false,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
      },
    },
  ];

  return (
    <>
      <Section title={'Pagamento de Amortização'}>
        <div>
          <Typography variant="body2">{'Percentual de Amortização sobre:'}</Typography>
          <Typography variant="body1">
            {percentualAmortizacaoDescriptions[eventosFinanceiros.amortizacaoSobre]}
          </Typography>
          <SmSpace />
        </div>
        <div>
          <Typography variant="body2">
            {
              'Percentual de Amortização passível de alteração e sujeito à disponibilidade de recursos previsto no Termo de Securitização?'
            }
          </Typography>
          <Typography variant="body1">
            {eventosFinanceiros.amortizacaoPassivelAoTermoSecuritizacao ? 'Sim' : 'Não'}
          </Typography>
          <SmSpace />
        </div>
        <div>
          <Typography variant="body2">{'Modalidade de Amortização:'}</Typography>
          <Typography variant="body1">
            {eventosFinanceiros.modalidadeAmortizacao ?? ModalidadeAmortizacao.CUSTOMIZADO}
          </Typography>
          <SmSpace />
        </div>
        <div style={{ height: 400, width: 440 }}>
          <DataGrid
            rows={sortedAmortizacoes}
            columns={getAmortizacaoGridColumns}
            pageSize={pageSize}
            localeText={localizedTextsMap}
          />
        </div>
      </Section>
      {sortedPagamentoJurosDates?.length > 0 && (
        <Section title={'Pagamento de Juros'}>
          <div style={{ height: 400, width: 260 }}>
            <DataGrid
              rows={sortedPagamentoJurosDates}
              columns={getPagamentoJurosGridColumns}
              pageSize={pageSize}
              localeText={localizedTextsMap}
            />
          </div>
        </Section>
      )}
      {sortedIncorporacaoJurosDates?.length > 0 && (
        <Section title={'Incorporação de Juros'}>
          <div>
            <Typography variant="body2">{'Data(s) Incorporação de Juros:'}</Typography>
            {sortedIncorporacaoJurosDates.map((data, i) => (
              <Typography key={i} variant="body1">
                {moment(data).format('DD/MM/YYYY')}
              </Typography>
            ))}
            <SmSpace />
          </div>
        </Section>
      )}
      <Section title={'Resgate Antecipado Facultativo'}>
        <div>
          <Typography variant="body2">{'Apresenta Resgate Antecipado Facultativo?'}</Typography>
          <Typography variant="body1">{eventosFinanceiros.possuiResgateAntecipado ? 'Sim' : 'Não'}</Typography>
          <SmSpace />
        </div>
      </Section>
    </>
  );
};
